import { Component, Input, ViewChild, ElementRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { noop } from 'rxjs';
import { Store } from '@ngrx/store';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  BaseCustomerInquiryOfferInterface,
  BaseOfferStatus,
  baseInquiryActions,
  baseAssignmentsActions,
  BaseCoreState,
  CustomerInquiryOfferProfileListItemResponse,
  BaseModalHeaderSizes,
  BaseInquiryShiftsWithFlex,
  AssignmentCustomerResponse,
  InquiryResponse,
  BaseModalService,
  BaseInquiryService,
  RequirementCategoryResponse,
  IndustrySurchargeResponse,
  RequirementResponse,
  OfferBubbleValues,
  ShiftTimeAndFlexResponse,
  BaseShiftIconsEnum,
  BaseApplication,
  BaseEnvironmentService,
} from 'base';
import { BaseCustomerOfferActions } from '../../../_enums/base-customer-offer-actions.enum';
import { BaseCustomerLocationWorkCouncilSettingsInterface } from '../../../../../shared/_interfaces/base-customer-location-work-council-settings.interface';

@Component({
  selector: 'base-customer-inquiry-offer',
  templateUrl: './base-customer-inquiry-offer.component.html',
})
export class BaseCustomerInquiryOfferComponent implements OnInit {
  @ViewChild('offerInfoPopup') offerInfoPopup: ElementRef;
  @ViewChild('industrySurchargesModal') industrySurchargesModal: ElementRef;
  @ViewChild('requirementsPopup') requirementsPopup: ElementRef;

  @Input() offer: BaseCustomerInquiryOfferInterface;
  @Input() modalRef: NgbModalRef;
  @Input() canAcceptOffers = false;
  @Input() isOfferPreselectionAllowed = false;
  @Input() assignmentInfo: AssignmentCustomerResponse;
  @Input() inquiry: Partial<InquiryResponse> = {};
  @Input() hasAdminAssignmentsPermission = false;
  @Input() shiftTimesWithFlex: (ShiftTimeAndFlexResponse & { index: number; label?: string })[] = [];

  offerStatuses = BaseOfferStatus;
  baseInquiryShiftsWithFlex = BaseInquiryShiftsWithFlex;
  offerAction: BaseCustomerOfferActions;
  baseShiftIconsEnum = BaseShiftIconsEnum;
  toAssignmentsTab = false;
  offerProfileRequirements: RequirementResponse[] = [];
  locationWorkCouncilSettings: BaseCustomerLocationWorkCouncilSettingsInterface;
  application: BaseApplication;

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    public baseInquiryService: BaseInquiryService,
    private environmentService: BaseEnvironmentService,
    private router: Router
  ) {}

  ngOnInit() {
    this.application = this.environmentService.getLocalValue('application').app;
    this.locationWorkCouncilSettings = {
      workCouncilActivated: !!this.inquiry.customerLocation?.workCouncilActivated,
      acceptOfferAfterPreselection: !!this.inquiry.customerLocation?.acceptOfferAfterPreselection,
      acceptOfferAfterWorkCouncilApproval: !!this.inquiry.customerLocation?.acceptOfferAfterWorkCouncilApproval,
    };
  }

  handleOfferSelection(offerId: number, offerStatus?: number): void {
    if (offerStatus === BaseOfferStatus.declined) {
      return;
    }
    this.store.dispatch(baseInquiryActions.selectCustomerInquiryOffer({ payload: { offerId } }));
  }

  openOfferInfoModal(offer: BaseCustomerInquiryOfferInterface, toOperation: boolean = false, event?: Event): void {
    event?.stopPropagation();

    if (!offer.data.isSelected) {
      this.handleOfferSelection(offer.data.id, offer.data.status as number);
    }

    this.toAssignmentsTab = toOperation;

    if (
      offer.data.assignment &&
      offer.data.status === this.offerStatuses.confirmed &&
      this.hasAdminAssignmentsPermission
    ) {
      this.store.dispatch(baseAssignmentsActions.loadAssignment({ payload: { assignment: offer.data.assignment } }));
    }

    this.modalRef = this.modalService.open(
      {
        hideHeader: true,
        bodyTemplate: this.offerInfoPopup,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'xl',
      }
    );

    this.modalRef.result.then(noop, noop);
  }

  openRequirementsModal(event: Event, profile: CustomerInquiryOfferProfileListItemResponse): void {
    event.stopPropagation();
    this.offerProfileRequirements = this.baseInquiryService.ungroupRequirements(profile.requirements);
    this.modalRef = this.modalService.open(
      {
        headText: !!this.inquiry.requirements?.length ? 'inquiry.requirementsFor' : 'inquiry.noRequirementsSet',
        headTextTranslateParams: { fullName: `${profile.firstName} ${profile.lastName}` },
        headerSize: BaseModalHeaderSizes.sm,
        bodyTemplate: this.requirementsPopup,
        okText: 'action.close',
        cancelButtonDisplayed: false,
        cancelButtonOnly: true,
      },
      {
        scrollable: false,
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.modalRef.result.then(noop, noop);
  }

  acceptOrRejectOfferFromInfoModal(event: BaseCustomerOfferActions): void {
    this.offerAction = event;
  }

  allRequirementsFulfilled(
    offerProfileRequirements: RequirementCategoryResponse[],
    inquiryRequirements: RequirementCategoryResponse[] = []
  ): boolean {
    return this.baseInquiryService
      .ungroupRequirements(inquiryRequirements)
      .every((inquiryRequirement) =>
        this.baseInquiryService
          .ungroupRequirements(offerProfileRequirements)
          .some((profileRequirement) => profileRequirement.id === inquiryRequirement.id)
      );
  }

  openIndustrySurchargeModal(event: Event, surcharges?: null | IndustrySurchargeResponse): void {
    event.stopPropagation();
    if (!surcharges) {
      return;
    }

    this.modalService.open(
      {
        headText: 'industrySurcharges.plural',
        bodyTemplate: this.industrySurchargesModal,
        cancelButtonDisplayed: true,
        okButtonDisplayed: false,
      },
      {
        scrollable: false,
        backdrop: 'static',
        size: 'lg',
      }
    );
  }

  openNoteFromProviderModal(event: Event, offer: BaseCustomerInquiryOfferInterface): void {
    event.stopPropagation();
    if (offer.data.note) {
      this.modalRef = this.modalService.open(
        {
          headText: 'Note from Provider',
          bodyText: offer.data.note,
          okText: 'action.close',
          cancelButtonDisplayed: false,
          cancelButtonOnly: true,
        },
        {
          scrollable: false,
          backdrop: 'static',
          size: 'lg',
        }
      );

      this.modalRef.result.then(noop, noop);
    }
  }

  onFlexLabelClick(event: Event, offer: BaseCustomerInquiryOfferInterface) {
    event.stopPropagation();
    if (offer.data.isSelected) {
      const areFlexShiftsAvailable = offer.dates.some((date) =>
        date.shifts.some(
          (shift) => shift.isFlexibleShift && shift.currentValue === OfferBubbleValues.OfferedByServiceProvider
        )
      );
      if (areFlexShiftsAvailable) {
        this.store.dispatch(
          baseInquiryActions.customerOfferFlexShiftsSelection({ payload: { offerId: offer.data.id } })
        );
      }
    }
  }

  onShiftIconClick(event: Event, offer: BaseCustomerInquiryOfferInterface, shiftType: BaseInquiryShiftsWithFlex) {
    event.stopPropagation();

    if (offer.data.isSelected) {
      const areFixedShiftsAvailable = offer.dates.some((date) =>
        date.shifts.some(
          (shiftItem) =>
            !shiftItem.isFlexibleShift &&
            shiftItem.shift === shiftType &&
            shiftItem.currentValue === OfferBubbleValues.OfferedByServiceProvider
        )
      );
      if (areFixedShiftsAvailable) {
        this.store.dispatch(
          baseInquiryActions.customerOfferFixedShiftsSelection({
            payload: { offerId: offer.data.id as number, shiftType },
          })
        );
      }
    }
  }

  openIzsInfoModal(event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/service-provider'], {
      queryParams: {
        page: 1,
        search: this.offer.data.pspBranch.name,
        pspBranchId: this.offer.data.pspBranch.id,
        location: this.inquiry?.customerLocation?.id,
      },
    });
  }
}
